import { cn } from '@lair-finance/util'
import { useChainId } from '@lair-finance/wagmi'
import { ChainById, ChainImages, ChainNames, SupportedChains } from '@/domains/chain'
import { useState } from 'react'
import { Button, Popover, PopoverClose, PopoverContent, PopoverOverlay, PopoverPortal, PopoverTrigger } from '@lair-finance/uikit'
import { useResponsive } from '@/shared/hooks/useResponsive.ts'
import { wagmiSwitchChain } from '@/shared/lib/wagmi/api/wagmiSwitchChain.ts'
import { useModalControlStore } from '@/domains/header/model/store.ts'

export function SwitchChainButton() {
  const isDesktop = useResponsive('desktop')
  const isTablet = useResponsive('tablet')
  const chainId = useChainId()
  const isTestnet = ChainById[chainId]?.testnet || false
  const icon = chainId ? ChainImages[chainId] : undefined
  const [activeModal, openModal, closeModal] = useModalControlStore(state => [state.activeModal, state.openModal, state.closeModal])
  const [loading, setLoading] = useState(false)

  if (!icon) return null

  const trigger = (
    <Button
      variant={'Outline'}
      className={'relative inline-flex-center size-9 sm:size-12 rounded-[8px] sm:rounded-[12px] px-0 overflow-hidden'}
      focus={activeModal === 'switchChain'}
    >
      {loading ? (
        <LoadingIcon className={'animate-spin'} size={20} progress={0.7} />
      ) : (
        <>
          <img src={icon} width={isTablet ? 28 : 20} height={isTablet ? 28 : 20} className={'rounded-[8px]'} />
          {isTestnet && (
            <div
              className={
                'absolute bottom-0 left-0 w-full h-[16px] bg-greyscale-500 rounded-b-[8px] sm:rounded-b-[12px] typo-new-label-3 text-greyscale-100'
              }
            >
              Test
            </div>
          )}
        </>
      )}
    </Button>
  )

  const content = (
    <div
      className={
        'flex flex-col w-screen md:w-[180px] bg-greyscale-600 md:border border-greyscale-500 md:rounded-[16px] overflow-hidden [&>button+button]:border-t [&>button+button]:border-greyscale-500'
      }
    >
      {SupportedChains.map(chain => (
        <SwitchChain
          key={chain.id}
          chainId={chain.id}
          onBefore={() => {
            setLoading(true)
            closeModal()
          }}
          onSettled={() => setLoading(false)}
        />
      ))}
    </div>
  )

  return (
    <Popover open={activeModal === 'switchChain'} onOpenChange={open => openModal(open ? 'switchChain' : null)}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      {!isDesktop && (
        <PopoverPortal>
          <PopoverClose asChild>
            <PopoverOverlay />
          </PopoverClose>
        </PopoverPortal>
      )}
      <PopoverContent sideOffset={isDesktop ? 4 : 14} side={'bottom'} align={'end'} preventCloseOnOutsideClick noStyle>
        {content}
      </PopoverContent>
    </Popover>
  )
}

function SwitchChain({
  className,
  chainId,
  onBefore,
  onSettled,
}: {
  className?: string
  chainId: number
  onBefore?: () => void
  onSettled?: () => void
}) {
  async function handleClick() {
    onBefore?.()
    try {
      await wagmiSwitchChain(chainId)
    } finally {
      onSettled?.()
    }
  }

  return (
    <button
      className={cn('group inline-flex items-center gap-x-2 sm:gap-x-3 hover:bg-greyscale-500 px-5 sm:px-4 h-[64px] sm:h-[56px]', className)}
      onClick={handleClick}
    >
      <img src={ChainImages[chainId]} width={28} height={28} className={'rounded-[8px] border border-greyscale-500'} alt="" />
      <span className={'text-greyscale-300 group-hover:text-greyscale-100 typo-new-label-2'}>{ChainNames[chainId]}</span>
    </button>
  )
}

function LoadingIcon({ className, size, progress }: { className?: string; size: number; progress: number }) {
  const strokeWidth = 12
  const radius = 40
  const circumference = 2 * Math.PI * radius
  const strokeDashoffset = circumference * (1 - progress)

  return (
    <svg className={className} width={size} height={size} viewBox="0 0 100 100">
      <circle cx="50" cy="50" r={radius} stroke="#1E2023" strokeWidth={strokeWidth} fill="none" />
      <circle
        cx="50"
        cy="50"
        r={radius}
        stroke="#FFFFFF"
        strokeWidth={strokeWidth}
        fill="none"
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        strokeLinecap="round"
        transform="rotate(-90 50 50)"
      />
    </svg>
  )
}
